import React from 'react';
import { formatDateOnly } from '../utils/dateUtils';

const MetaBudgetReviewList = ({ 
  records, 
  onEdit, 
  onDelete, 
  onShowHistory, 
  onQuickAction, 
  currentUser,
  getBudgetStatusColor,
  getBudgetStatusMessage 
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  // Usar la función de dateUtils que maneja correctamente las fechas sin problemas de zona horaria
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') return 'N/A';
    return formatDateOnly(dateString);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pendiente': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'en_proceso': 'bg-blue-100 text-blue-800 border-blue-200',
      'completado': 'bg-green-100 text-green-800 border-green-200',
      'cancelado': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusText = (status) => {
    const texts = {
      'pendiente': 'Pendiente',
      'en_proceso': 'En Proceso',
      'completado': 'Completado',
      'cancelado': 'Cancelado'
    };
    return texts[status] || status;
  };

  const calculateDaysRemaining = (fechaVencimiento) => {
    if (!fechaVencimiento) return 0;
    const today = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diffTime = vencimiento - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="text-gray-400 text-6xl mb-4">💰</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay registros de presupuesto</h3>
        <p className="text-gray-500">Comienza creando tu primer registro de revisión de presupuesto.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cliente / Campaña
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fechas / Agente
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Presupuesto
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Saldo / Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {records.map((record) => {
              const porcentajeRestante = parseFloat(record.porcentaje_restante) || 0;
              const diasRestantes = calculateDaysRemaining(record.fecha_vencimiento);
              const budgetStatusColor = getBudgetStatusColor(porcentajeRestante);
              const budgetStatusMessage = getBudgetStatusMessage(porcentajeRestante);

              return (
                <tr 
                  key={record.id} 
                  className={`hover:bg-gray-50 transition-colors ${budgetStatusColor} border-l-4`}
                >
                  <td className="px-4 py-4">
                    <div className="space-y-1">
                      <div className="font-medium text-gray-900 text-sm">
                        {record.cliente}
                      </div>
                      <div className="text-xs text-gray-600">
                        <div><strong>Campaña:</strong> {record.nombre_campana || 'N/A'}</div>
                        <div>
                          <strong>Cuenta:</strong> {
                            record.cuenta_publicitaria && record.cuenta_publicitaria !== 'N/A' ? (
                              <a 
                                href={record.cuenta_publicitaria.startsWith('http') ? record.cuenta_publicitaria : `https://${record.cuenta_publicitaria}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline ml-1"
                                title="Abrir cuenta publicitaria"
                              >
                                {record.cuenta_publicitaria}
                                <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            ) : (
                              <span className="ml-1">N/A</span>
                            )
                          }
                        </div>
                        <div><strong>Célula:</strong> {record.celula || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="space-y-1 text-xs">
                      <div><strong>Pago:</strong> {formatDate(record.fecha_pago)}</div>
                      <div><strong>Vence:</strong> {formatDate(record.fecha_vencimiento)}</div>
                      <div><strong>Días restantes:</strong> {diasRestantes}</div>
                      <div><strong>Agente:</strong> {record.agente_seguimiento || 'N/A'}</div>
                      <div><strong>Mes:</strong> {record.mes || 'N/A'}</div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="space-y-1 text-xs">
                      <div><strong>Monto Total:</strong></div>
                      <div className="font-semibold text-blue-600">
                        {formatCurrency(record.monto_pago)}
                      </div>
                      <div><strong>Presupuesto Diario:</strong></div>
                      <div className="font-medium text-green-600">
                        {formatCurrency(record.presupuesto_diario)}
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="space-y-2">
                      <div className="text-xs">
                        <div><strong>Saldo Disponible:</strong></div>
                        <div className="font-semibold text-lg">
                          {formatCurrency(record.saldo_disponible)}
                        </div>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Porcentaje restante:</span>
                          <span className="font-medium">{porcentajeRestante.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              porcentajeRestante >= 70 ? 'bg-green-500' :
                              porcentajeRestante >= 40 ? 'bg-yellow-500' :
                              porcentajeRestante >= 20 ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.max(0, Math.min(100, porcentajeRestante))}%` }}
                          ></div>
                        </div>
                        <div className="text-xs font-medium text-center">
                          {budgetStatusMessage}
                        </div>
                        <div className="text-xs text-gray-500 text-center mt-1">
                          {porcentajeRestante >= 90 ? '🟢 Excelente - 90-100%' :
                           porcentajeRestante >= 80 ? '🟡 Muy Bueno - 80-89%' :
                           porcentajeRestante >= 70 ? '🟠 Bueno - 70-79%' :
                           porcentajeRestante >= 60 ? '🔶 Regular - 60-69%' :
                           porcentajeRestante >= 50 ? '🔸 Medio - 50-59%' :
                           porcentajeRestante >= 40 ? '🔹 Bajo - 40-49%' :
                           porcentajeRestante >= 30 ? '🔺 Muy Bajo - 30-39%' :
                           porcentajeRestante >= 20 ? '🔻 Crítico - 20-29%' :
                           porcentajeRestante >= 10 ? '🚨 Urgente - 10-19%' :
                           '⛔ Agotado - 0-9%'}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(record.estado)}`}>
                      {getStatusText(record.estado)}
                    </span>
                  </td>

                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => onQuickAction(record)}
                        className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all duration-200"
                        title="Acción Rápida"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onShowHistory(record)}
                        className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all duration-200"
                        title="Ver Historial"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onEdit(record)}
                        className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                        title="Editar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {(currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
                        <button
                          onClick={() => onDelete(record.id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                          title="Eliminar"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Resumen de estadísticas */}
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>Total de registros: {records.length}</span>
          <div className="flex space-x-4">
            <span>Saludables: {records.filter(r => (parseFloat(r.porcentaje_restante) || 0) >= 70).length}</span>
            <span>En seguimiento: {records.filter(r => {
              const p = parseFloat(r.porcentaje_restante) || 0;
              return p >= 40 && p < 70;
            }).length}</span>
            <span>Bajo: {records.filter(r => {
              const p = parseFloat(r.porcentaje_restante) || 0;
              return p >= 20 && p < 40;
            }).length}</span>
            <span>Crítico: {records.filter(r => (parseFloat(r.porcentaje_restante) || 0) < 20).length}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaBudgetReviewList;
